import React from "react";
import { X, Shield, FileText } from "lucide-react";

// Brand / contact details. Update CONTACT_EMAIL to your real support address.
const APP_NAME = "Xunzhine";
const SITE = "xunzhine.in";
const CONTACT_EMAIL = "contact@xunzhine.in";
const EFFECTIVE_DATE = "June 2026";

const H = ({ children }) => (
  <h3 className="text-cyan-300 font-semibold mt-5 mb-1.5">{children}</h3>
);
const P = ({ children }) => (
  <p className="text-gray-300 text-sm leading-relaxed mb-2">{children}</p>
);
const LI = ({ children }) => (
  <li className="text-gray-300 text-sm leading-relaxed">{children}</li>
);

const PrivacyContent = () => (
  <div>
    <P>Effective date: {EFFECTIVE_DATE}</P>
    <P>
      This Privacy Policy explains how {APP_NAME} ("we", "us"), accessible at{" "}
      {SITE}, handles information when you use the app.
    </P>

    <H>Information we handle</H>
    <ul className="list-disc pl-5 space-y-1">
      <LI>
        <strong>Your content:</strong> the challenges, goals, reminders,
        clipboard notes, quick links and conference entries you create.
      </LI>
      <LI>
        <strong>Account identifiers:</strong> the username and passkey you enter
        to access the app.
      </LI>
      <LI>
        <strong>Usage analytics:</strong> if you accept cookies, anonymous usage
        data (page views, device type, approximate location, and feature events
        such as completing a goal) via Google Analytics.
      </LI>
    </ul>

    <H>Where your data is stored</H>
    <ul className="list-disc pl-5 space-y-1">
      <LI>
        <strong>On your device:</strong> by default your data is saved locally
        in your browser (local storage).
      </LI>
      <LI>
        <strong>In the cloud:</strong> for authorized cloud accounts, data is
        also stored with our secure cloud backend so it can sync across
        devices.
      </LI>
    </ul>

    <H>Cookies & analytics</H>
    <P>
      We use Google Analytics only after you click "Accept" on the cookie
      banner. You can decline, and you can change your choice at any time using
      "Cookie settings" in the footer. If you decline, no analytics cookies are
      set and no analytics data is sent.
    </P>

    <H>How we use information</H>
    <ul className="list-disc pl-5 space-y-1">
      <LI>To provide the app and save/sync your data.</LI>
      <LI>To understand usage and improve features (only with consent).</LI>
      <LI>To maintain security and prevent misuse.</LI>
    </ul>
    <P>We do not sell your personal data.</P>

    <H>Sharing</H>
    <P>
      We share data only with service providers that help us run the app
      (e.g. our cloud storage, Google for analytics, our hosting provider).
      They process data on our behalf.
    </P>

    <H>Your rights</H>
    <P>
      Depending on your location (including the EU/UK and other regions), you may
      have rights to access, correct, export or delete your data, and to
      withdraw consent. You can export or reset your data from within the app
      (Settings → Data Management), or contact us at {CONTACT_EMAIL}.
    </P>

    <H>Data retention</H>
    <P>
      Local data remains until you clear it or your browser storage is cleared.
      Cloud data remains until you delete it or request deletion.
    </P>

    <H>Children</H>
    <P>
      The app is not directed to children under 13 (or the minimum age in your
      country), and we do not knowingly collect their data.
    </P>

    <H>Changes</H>
    <P>
      We may update this policy; material changes will be reflected by updating
      the effective date above.
    </P>

    <H>Contact</H>
    <P>Questions? Email {CONTACT_EMAIL}.</P>

    <P>
      <em>
        This document is a general template provided for convenience and is not
        legal advice. Please have it reviewed for your specific obligations.
      </em>
    </P>
  </div>
);

const TermsContent = () => (
  <div>
    <P>Effective date: {EFFECTIVE_DATE}</P>
    <P>
      These Terms govern your use of {APP_NAME} at {SITE}. By using the app you
      agree to these Terms.
    </P>

    <H>Use of the app</H>
    <ul className="list-disc pl-5 space-y-1">
      <LI>
        You are responsible for the content you create and for keeping your
        passkey confidential.
      </LI>
      <LI>
        You agree not to misuse the app, attempt to disrupt it, or use it for
        unlawful purposes.
      </LI>
    </ul>

    <H>Accounts & access</H>
    <P>
      Some features (such as cloud sync) may require an authorized account or a
      paid plan. We may modify, suspend, or discontinue features at any time.
    </P>

    <H>Your data</H>
    <P>
      You retain ownership of the content you create. You are responsible for
      keeping your own backups; use the in-app Export feature regularly. While we
      take reasonable measures to protect data, we cannot guarantee against all
      loss.
    </P>

    <H>Third-party services</H>
    <P>
      The app relies on third-party providers (e.g. hosting, cloud storage,
      Google Analytics). Their services are governed by their own terms.
    </P>

    <H>Disclaimer & limitation of liability</H>
    <P>
      The app is provided "as is" without warranties of any kind. To the maximum
      extent permitted by law, we are not liable for any indirect or
      consequential damages, or for any loss of data, arising from your use of
      the app.
    </P>

    <H>Governing law</H>
    <P>
      These Terms are governed by the laws of India, without regard to conflict
      of law rules.
    </P>

    <H>Changes</H>
    <P>
      We may update these Terms; continued use after changes means you accept the
      updated Terms.
    </P>

    <H>Contact</H>
    <P>Questions? Email {CONTACT_EMAIL}.</P>

    <P>
      <em>
        This document is a general template provided for convenience and is not
        legal advice. Please have it reviewed for your specific obligations.
      </em>
    </P>
  </div>
);

const LegalModal = ({ isOpen, type, onClose }) => {
  if (!isOpen) return null;
  const isPrivacy = type === "privacy";
  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[70] p-4"
      onClick={onClose}
    >
      <div
        className="bg-gray-800 rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border-2 border-cyan-500/30 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-gray-700 sticky top-0 bg-gray-800 z-10">
          <h2 className="text-2xl font-bold text-yellow-400 flex items-center gap-2">
            {isPrivacy ? (
              <Shield className="w-6 h-6" />
            ) : (
              <FileText className="w-6 h-6" />
            )}
            {isPrivacy ? "Privacy Policy" : "Terms & Conditions"}
          </h2>
          <button
            onClick={onClose}
            className="text-cyan-300 hover:text-cyan-200 transition-colors"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        <div className="p-6">
          {isPrivacy ? <PrivacyContent /> : <TermsContent />}
        </div>
      </div>
    </div>
  );
};

export default LegalModal;
